import React from "react";
import { AlertCircle, AlertTriangle, CheckCircle2, Info } from "lucide-react";

import { Callout } from "@/components/ui/Callout";
import Skeleton from "@/components/shared/Skeleton";
import { plural } from "@/utils/formatText";

import { TONE } from "../constants";
import Chip from "./Chip";
import DetailDrawer from "./DetailDrawer";
import { OperationSummary } from "./OperationList";
import RevisionList from "./RevisionList";
import RevisionSqlBlock from "./RevisionSqlBlock";
import SchemaDiffPanel from "./SchemaDiffPanel";

// Where this database sits in the revision graph. `diverged` is the one that
// earns its own state: it is not fixed by upgrading, so telling somebody to
// press Upgrade would cost them an incident.
const POSITION = {
    current: {
        tone: TONE.emerald,
        icon: <CheckCircle2 size={11} />,
        label: "on the code head",
    },
    behind: {
        tone: TONE.amber,
        icon: <AlertCircle size={11} />,
        label: "behind the code head",
    },
    diverged: {
        tone: TONE.rose,
        icon: <AlertTriangle size={11} />,
        label: "revision not in this build",
    },
    unknown: {
        tone: TONE.slate,
        icon: <AlertCircle size={11} />,
        label: "no revision recorded",
    },
};

/**
 * What the schema revision on one database actually is.
 *
 * The column can only hold seven characters of a hash. This says what that
 * revision is called, what an upgrade would run and in what order, and — when
 * the revision is one this build has never heard of — that upgrading is not the
 * answer.
 */
const SchemaRevisionDrawer = ({ row, catalog, loading, onClose }) => {
    const current = row?.alembicCurrent ?? null;
    const head = row?.alembicHead ?? catalog?.head ?? null;
    const position = POSITION[row?.alembicPosition] ?? POSITION.unknown;
    const currentRevision = catalog?.revision(current);
    const headRevision = catalog?.revision(head);
    const pending = catalog?.describeRevisions(row?.pendingRevisions) ?? [];

    // The target string the schema comparison takes. The platform row's key is
    // the literal "platform"; a dedicated row needs the `tenant:` prefix or the
    // endpoint returns 400.
    const schemaTarget = row?.isPlatform
        ? "platform"
        : row?.tenantId
        ? `tenant:${row.tenantId}`
        : null;

    return (
        <DetailDrawer
            open={!!row}
            onClose={onClose}
            title={
                current
                    ? `Schema ${current.slice(0, 7)}${
                          currentRevision?.title ? ` — ${currentRevision.title}` : ""
                      }`
                    : "Schema — no revision recorded"
            }
            subtitle={row?.label}
        >
            {loading && <Skeleton className="h-24 w-full" />}

            {!loading && (
                <>
                    <div className="flex items-center gap-2 flex-wrap">
                        <Chip
                            tone={position.tone}
                            icon={position.icon}
                            label={position.label}
                        />
                        {head && (
                            <span className="text-[11px] text-text-secondary">
                                the code head is{" "}
                                <span className="font-mono" title={head}>
                                    {head.slice(0, 7)}
                                </span>
                                {headRevision?.title ? ` — ${headRevision.title}` : ""}
                            </span>
                        )}
                    </div>

                    {row?.alembicPosition === "diverged" && (
                        <Callout tone="warning" icon={AlertTriangle} size="xs">
                            <span className="font-medium block mb-0.5">
                                Upgrading will not fix this
                            </span>
                            This database is on{" "}
                            <span className="font-mono">{current}</span>, and no
                            migration file in this build carries that revision. Either
                            the server is running older code than the database was
                            migrated with, or a migration file was removed. Line the
                            code up with the database before running anything from this
                            screen.
                        </Callout>
                    )}

                    {row?.alembicPosition === "unknown" && (
                        <Callout tone="warning" icon={AlertCircle} size="xs">
                            <span className="font-medium block mb-0.5">
                                No revision is recorded on this database
                            </span>
                            The <span className="font-mono">alembic_version</span> table
                            is missing or empty, so where this database sits is not
                            known — it is not the same as being behind.
                        </Callout>
                    )}

                    {/* What the revision this database sits on actually did.
                        The migration's own docstring is not shown: it is typed by
                        hand, most files here have none, and where one exists it
                        restates what the SQL below already says exactly. */}
                    {currentRevision && (
                        <div>
                            <div className="text-xs font-medium text-text-secondary mb-1">
                                What this revision did
                            </div>
                            <div className="mb-2">
                                <OperationSummary revision={currentRevision} />
                            </div>
                            <RevisionSqlBlock
                                revision={currentRevision.revision}
                                fallbackOperations={currentRevision.upgradeOperations}
                            />
                        </div>
                    )}

                    {pending.length > 0 && (
                        <div>
                            <div className="text-xs font-medium text-text-secondary mb-2">
                                {plural(pending.length, "revision", "revisions")} an
                                upgrade would run, in order
                            </div>
                            <RevisionList revisions={pending} />
                        </div>
                    )}

                    {row?.alembicPosition === "current" && (
                        <Callout tone="neutral" icon={Info} size="xs">
                            Nothing is pending. This database carries every migration in
                            the build.
                        </Callout>
                    )}

                    {/* The revision above is a claim. This is where it gets
                        checked. */}
                    {schemaTarget && (
                        <div className="pt-2 border-t border-border-primary">
                            <SchemaDiffPanel target={schemaTarget} />
                        </div>
                    )}
                </>
            )}
        </DetailDrawer>
    );
};

export default SchemaRevisionDrawer;
